import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MobileContactsService } from '@contacts/feature';
import { CitySelectComponent } from '@sdvor/city/ui/v2';
import { SdBottomSheetService, SdvorIconComponent } from '@sdvor/shared/ui/v2';
import { IBottomSheetHeighOption, IconTypes, NavigationSourceService, SdBottomSheetOptions } from '@shared/v2';
import { Pos } from '@shop/const';
import { ShopWorkingTimeService } from '@shop/feature';
import { WindowRef } from '@spartacus/core';
import { GeocodeResult } from '@yandex-maps/const';
import { GeocodeService } from '@yandex-maps/feature';
import { BehaviorSubject, combineLatest, delay, map, Observable, startWith } from 'rxjs';
import { NoShopsComponent } from '../no-shops';
import { ShopDesktopComponent } from '../shop-desktop';
import { ShopMapComponent } from '../shop-map';
import { BottomSheetActivePlacemarkComponent, BottomSheetShopListComponent } from './bottom-sheet';

export interface IBottomSheetShopListData {
  shopsSorted$: Observable<Pos[]>;
  activeId$: BehaviorSubject<string>;
  clickShop: (id: string, zoom?: number) => void;
  changeAddress: (geocodeResult?: GeocodeResult) => void;
}

export interface IBottomSheetPlacemarkActiveData {
  shop: Pos;
  showAvailability?: boolean;
}

@Component({
  selector: 'sd-shop-mobile',
  imports: [SdvorIconComponent, NoShopsComponent, ShopMapComponent, CitySelectComponent],
  templateUrl: './shop-mobile.component.html',
  styleUrls: ['./shop-mobile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShopMobileComponent extends ShopDesktopComponent implements OnInit, OnChanges {
  activePlacemark$: BehaviorSubject<string> = new BehaviorSubject('');
  iconTypes = IconTypes;
  readonly DEFAULT_ZOOM = 14;

  bottomSheetShopListIsOpened = false;

  @Input() shops: Pos[];

  constructor(
    private mobileContactsService: MobileContactsService,
    private winRef: WindowRef,
    private navigationSourceService: NavigationSourceService,
    private bottomSheetService: SdBottomSheetService,
    geocodeService: GeocodeService,
    destroyRef: DestroyRef,
    shopWorkingTimeService: ShopWorkingTimeService,
    cdRef: ChangeDetectorRef,
  ) {
    super(geocodeService, shopWorkingTimeService, destroyRef, cdRef);
  }

  ngOnInit(): void {
    super.ngOnInit();

    if (this.shops) {
      this.shopsSubject$.next(this.shops);
    }

    combineLatest({
      activeId: this.activePlacemark$,
      shops: this.shopsSorted$.pipe(startWith([])),
      isOpenMobileContacts: this.mobileContactsService.isOpen$,
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(({ activeId, shops, isOpenMobileContacts }) => {
          return {
            shop: activeId ? (shops.find((shop) => shop.name === activeId) ?? null) : null,
            isOpenMobileContacts,
          };
        }),
        // Решение, когда из bottom-sheet открываем другой.
        delay(100),
      )
      .subscribe(({ shop, isOpenMobileContacts }) => {
        if (isOpenMobileContacts) {
          this.bottomSheetService.close();
          this.bottomSheetShopListIsOpened = false;
        } else {
          if (shop) {
            this.openBottomSheetActivePlacemark(shop);
          } else if (!this.bottomSheetShopListIsOpened) {
            this.openBottomSheetShopList();
          }
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
  }

  onClickArrowPrev(): void {
    this.navigationSourceService.goBack();
  }

  onClickShop(id: string): void {
    this.activeId$.next(id);
    // На мобильных клик по списку может произойти до события (created) у карты.
    // В этом случае не пытаемся работать с mapInstance, но продолжаем пользовательский сценарий.
    if (this.mapInstance) {
      this.mapInstance.setZoom(this.DEFAULT_ZOOM);
      this.selectShopMap(id, false);
    }
    this.onPlacemarkClick(id);
  }

  onPlacemarkClick(id: string): void {
    this.activePlacemark$.next(id);
  }

  onResetActivePlacemark(): void {
    this.activePlacemark$.next('');
  }

  openBottomSheetShopList(): void {
    if (!this.winRef.isBrowser()) {
      return;
    }

    this.bottomSheetShopListIsOpened = true;

    const data: IBottomSheetShopListData = {
      shopsSorted$: this.shopsSorted$,
      activeId$: this.activeId$,
      clickShop: (id: string) => this.onClickShop(id),
      changeAddress: (geocodeResult?: GeocodeResult) => this.onChangeAddress(geocodeResult),
    };

    const heightOptions: IBottomSheetHeighOption = {
      type: 'px',
      value: 80,
    };

    const options: SdBottomSheetOptions<IBottomSheetShopListData> = {
      breakpoints: [80],
      showTabbar: true,
      init: heightOptions,
      minHeight: heightOptions,
      closeDesktopDown: true,
      hasCloseButton: false,
      ignoreOutsideClick: false,
      blockOverlay: false,
      overlayDismissMode: 'collapse',
      background: 'white',
      data,
    };

    setTimeout(() => this.bottomSheetService.open(BottomSheetShopListComponent, options));
  }

  openBottomSheetActivePlacemark(shop: Pos): void {
    if (!this.winRef.isBrowser()) {
      return;
    }

    this.bottomSheetShopListIsOpened = false;

    const height = 200;
    const window = this.winRef.nativeWindow;

    const data: IBottomSheetPlacemarkActiveData = {
      shop,
    };

    const heightOptions: IBottomSheetHeighOption = {
      type: 'px',
      value: height,
    };

    const maxHeight = (height / (window?.innerHeight || height)) * 100;

    const options: SdBottomSheetOptions<IBottomSheetPlacemarkActiveData> = {
      breakpoints: [maxHeight],
      showTabbar: true,
      init: heightOptions,
      minHeight: heightOptions,
      closeDesktopDown: true,
      hasCloseButton: true,
      ignoreOutsideClick: false,
      blockOverlay: false,
      background: 'white',
      data,
      onClose: () => this.onResetActivePlacemark(),
    };

    this.bottomSheetService.open(BottomSheetActivePlacemarkComponent, options);
  }
}
